// @ts-check

/** @file Creates the durable frontend application replica and mutation queue. */

import Migration from "velocious/build/src/database/migration/index.js"

const REPLICA_TABLES_IN_DROP_ORDER = [
  "syncs",
  "replica_metadata",
  "comments",
  "timelogs",
  "task_assigned_users",
  "task_boards",
  "tasks",
  "board_columns",
  "boards",
  "task_types",
  "projects",
  "users"
]

/** Creates the ten-resource local replica and its durable mutation bookkeeping. */
export default class CreateLocalReplica extends Migration {
  /** Creates every table and query index inside one retryable SQLite transaction. */
  async up() {
    await this.connection().transaction(async () => {
      await this.createReplicaTables()
      await this.createReplicaIndexes()
    })
  }

  /** Drops only application-replica state, preserving installation/session options. */
  async down() {
    await this.connection().transaction(async () => {
      for (const tableName of REPLICA_TABLES_IN_DROP_ORDER) {
        if (await this.tableExists(tableName)) await this.dropTable(tableName)
      }
    })
  }

  /** Creates the local tables in dependency order without foreign-key constraints. */
  async createReplicaTables() {
    await this.createTableUnlessExists("users", (t) => {
      t.string("email", {null: true})
      t.string("first_name", {null: true})
      t.string("last_name", {null: true})
      t.string("locale", {null: true})
      t.boolean("active", {null: true})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("projects", (t) => {
      t.string("name", {null: true})
      t.string("state", {default: "active", null: true})
      t.text("description", {null: true})
      t.datetime("deadline_at", {null: true})
      t.uuid("organization_id", {null: true})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("task_types", (t) => {
      t.uuid("project_id", {null: true})
      t.string("name", {null: true})
      t.boolean("default_for_project", {default: false, null: false})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("boards", (t) => {
      t.uuid("project_id", {null: false})
      t.string("name", {default: "Board", null: false})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("board_columns", (t) => {
      t.uuid("board_id", {null: false})
      t.string("name", {null: false})
      t.string("color", {null: true})
      t.integer("position", {null: false})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("tasks", (t) => {
      t.uuid("project_id", {null: true})
      t.uuid("task_type_id", {null: true})
      t.uuid("user_id", {null: true})
      t.uuid("parent_id", {null: true})
      t.string("name", {null: true})
      t.text("description", {null: true})
      t.string("state", {default: "open", null: true})
      t.integer("priority", {null: true})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("task_boards", (t) => {
      t.uuid("board_id", {null: false})
      t.uuid("board_column_id", {null: false})
      t.uuid("task_id", {null: false})
      t.integer("row_number", {null: false})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("task_assigned_users", (t) => {
      t.uuid("task_id", {null: true})
      t.uuid("user_id", {null: true})
      t.uuid("user_assigned_by_id", {null: true})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("comments", (t) => {
      t.uuid("user_id", {null: true})
      t.string("resource_type", {null: true})
      t.uuid("resource_id", {null: true})
      t.bigint("id_per_resource", {null: true})
      t.text("comment", {null: true})
      t.string("automation_kind", {null: true})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("timelogs", (t) => {
      t.uuid("task_id", {null: true})
      t.uuid("user_id", {null: true})
      t.addColumn("date", {isNewColumn: true, null: true, type: "date"})
      t.bigint("time_seconds", {null: true})
      t.bigint("time_transport_seconds", {null: true})
      t.bigint("transport_length", {null: true})
      t.text("comment", {null: true})
      t.boolean("invoiced", {default: false, null: false})
      this.addReplicaTimestamps(t)
    })

    await this.createTableUnlessExists("replica_metadata", (t) => {
      t.string("key", {null: false})
      t.text("value", {null: false})
      t.timestamps({null: false})
    })

    await this.createTableUnlessExists("syncs", (t) => {
      t.string("resource_type", {null: false})
      t.uuid("resource_id", {null: false})
      t.string("sync_type", {null: false})
      t.string("command", {null: true})
      t.json("data", {null: false})
      t.json("base_version", {null: true})
      t.uuid("predecessor_mutation_id", {null: true})
      t.bigint("sequence", {null: false})
      t.string("state", {default: "pending", null: false})
      t.string("failure_reason", {null: true})
      t.json("failure_details", {null: true})
      t.timestamps({null: false})
    })
  }

  /** Creates lookup and uniqueness indexes used by local read and queue paths. */
  async createReplicaIndexes() {
    const indexes = [
      ["boards", ["project_id"]],
      ["board_columns", ["board_id"]],
      ["board_columns", ["board_id", "position"], {unique: true}],
      ["task_types", ["project_id"]],
      ["tasks", ["project_id"]],
      ["tasks", ["task_type_id"]],
      ["tasks", ["user_id"]],
      ["tasks", ["parent_id"]],
      ["task_boards", ["board_id"]],
      ["task_boards", ["board_column_id"]],
      ["task_boards", ["task_id"]],
      ["task_boards", ["board_column_id", "row_number"], {unique: true}],
      ["task_boards", ["task_id", "board_id"], {unique: true}],
      ["task_assigned_users", ["task_id"]],
      ["task_assigned_users", ["user_id"]],
      ["task_assigned_users", ["user_assigned_by_id"]],
      ["task_assigned_users", ["task_id", "user_id"], {unique: true}],
      ["comments", ["resource_type", "resource_id", "created_at"]],
      ["comments", ["resource_type", "resource_id", "id_per_resource"]],
      ["comments", ["user_id"]],
      ["timelogs", ["task_id", "date"]],
      ["timelogs", ["user_id", "date"]],
      ["timelogs", ["date"]],
      ["replica_metadata", ["key"], {unique: true}],
      ["syncs", ["state", "sequence"]],
      ["syncs", ["resource_type", "resource_id", "sequence"]],
      ["syncs", ["predecessor_mutation_id"]]
    ]

    for (const [tableName, columns, options] of indexes) {
      await this.addIndex(
        /** @type {string} */ (tableName),
        /** @type {string[]} */ (columns),
        {...(/** @type {{unique?: boolean} | undefined} */ (options) || {}), ifNotExists: true}
      )
    }
  }

  /**
   * Creates one UUID-primary-key table only when absent.
   * @param {string} tableName Name of the table to create.
   * @param {(table: import("velocious/build/src/database/table-data/index.js").default) => void} callback Table definition.
   */
  async createTableUnlessExists(tableName, callback) {
    if (await this.tableExists(tableName)) return

    await this.createTable(tableName, {id: {type: "uuid"}, ifNotExists: true}, callback)
  }

  /**
   * Adds server timestamps and client-only application metadata.
   * @param {import("velocious/build/src/database/table-data/index.js").default} table Table definition.
   */
  addReplicaTimestamps(table) {
    table.timestamps({null: false})
    table.datetime("last_sync_change_at", {null: true})
  }
}
